import React from 'react';
import DodTermText from '../DodTermText';
import {
  FORMATION_COUNTRY_CODES,
  US_FORMATION_STATES,
  US_FORMATION_TERRITORIES,
  formationCountryName,
  type BusinessQuestionId,
  type FormationLanguage,
  answerBusinessQuestion,
  businessCreationAnswerLines,
  businessQuestionNumber,
  nextBusinessQuestion,
  retreatBusinessAnswers,
} from '../constants/businessCreationInterview';
import type { BusinessCreationAnswers } from '../constants/complianceChecklists';

type InterviewProps = {
  answers: BusinessCreationAnswers;
  language: FormationLanguage;
  onAnswer: (id: BusinessQuestionId, value: string) => void;
  onBack: () => void;
  onStartOver: () => void;
};

const BusinessCreationInterview: React.FC<InterviewProps> = ({
  answers,
  language,
  onAnswer,
  onBack,
  onStartOver,
}) => {
  const question = nextBusinessQuestion(answers, language);
  const spanish = language === 'es';
  const number = businessQuestionNumber(answers);
  const [draft, setDraft] = React.useState('');
  const canGoBack = number > 1;

  React.useEffect(() => {
    if (!question) {
      setDraft('');
      return;
    }
    const current = answers[question.id];
    setDraft(typeof current === 'string' ? current : '');
  }, [answers, question]);

  if (!question) {
    return null;
  }

  const submitDraft = (event: React.FormEvent) => {
    event.preventDefault();
    const value = draft.trim();
    if (!value) {
      return;
    }
    onAnswer(question.id, value);
  };

  const fieldLabel = (() => {
    if (question.kind === 'state') {
      return answers.origin === 'territory'
        ? (spanish ? 'Territorio' : 'Territory')
        : (spanish ? 'Estado' : 'State');
    }
    if (question.kind === 'country') {
      return spanish ? 'País' : 'Country';
    }
    if (question.kind === 'text') {
      return question.placeholder || (spanish ? 'Su respuesta' : 'Your answer');
    }
    return '';
  })();

  return (
    <form className="business-creation-interview is-lz" onSubmit={submitDraft} aria-labelledby="business-creation-prompt">
      <header className="business-creation-lz-chrome" aria-hidden="true">
        <span className="business-creation-lz-brand">
          {spanish ? 'Formar mi empresa' : 'Form my company'}
        </span>
        <span className="business-creation-lz-step">
          {spanish ? `Paso ${number}` : `Step ${number}`}
        </span>
      </header>

      <div className="business-creation-lz-card">
        <h1 id="business-creation-prompt" className="business-creation-lz-title">
          {question.prompt}
        </h1>

        {question.kind === 'choice' && question.options ? (
          <ul className="business-creation-options">
            {question.options.map((option) => (
              <li key={option.value} className="business-creation-option-row">
                <button
                  type="button"
                  className="business-creation-option"
                  onClick={() => onAnswer(question.id, option.value)}
                >
                  <span className="business-creation-option-label">{option.label}</span>
                </button>
                {option.detail ? (
                  <p className="business-creation-option-detail">
                    <DodTermText text={option.detail} language={language} />
                  </p>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}

        {question.kind === 'state' ? (
          <label className="business-creation-field" htmlFor="business-creation-state">
            {fieldLabel}
            <select
              id="business-creation-state"
              value={draft}
              required
              onChange={(event) => {
                setDraft(event.target.value);
                if (event.target.value) {
                  onAnswer(question.id, event.target.value);
                }
              }}
            >
              <option value="">{question.placeholder}</option>
              {(answers.origin === 'territory' ? US_FORMATION_TERRITORIES : US_FORMATION_STATES).map(([code, name]) => (
                <option key={code} value={code}>{name}</option>
              ))}
            </select>
          </label>
        ) : null}

        {question.kind === 'country' ? (
          <label className="business-creation-field" htmlFor="business-creation-country">
            {fieldLabel}
            <select
              id="business-creation-country"
              value={draft}
              required
              onChange={(event) => {
                setDraft(event.target.value);
                if (event.target.value) {
                  onAnswer(question.id, event.target.value);
                }
              }}
            >
              <option value="">{question.placeholder}</option>
              {[...FORMATION_COUNTRY_CODES]
                .sort((left, right) => formationCountryName(left, language).localeCompare(formationCountryName(right, language)))
                .map((code) => (
                  <option key={code} value={code}>{formationCountryName(code, language)}</option>
                ))}
            </select>
          </label>
        ) : null}

        {question.kind === 'text' ? (
          <label className="business-creation-field" htmlFor="business-creation-text">
            {fieldLabel}
            <input
              id="business-creation-text"
              value={draft}
              required
              autoFocus
              placeholder={question.placeholder}
              onChange={(event) => setDraft(event.target.value)}
            />
          </label>
        ) : null}

        {question.help ? (
          <aside className="business-creation-lz-tip" role="note">
            <DodTermText text={question.help} language={language} />
            <p className="business-creation-lz-tip-foot">
              {spanish
                ? 'Esto no presenta nada ante un gobierno.'
                : 'This does not file anything with a government.'}
            </p>
          </aside>
        ) : null}
      </div>

      <div className="business-creation-actions">
        {canGoBack ? (
          <button type="button" className="business-creation-lz-back" onClick={onBack}>
            {spanish ? 'Atrás' : 'Back'}
          </button>
        ) : (
          <span />
        )}
        <div className="business-creation-lz-actions-end">
          {canGoBack ? (
            <button
              type="button"
              className="business-creation-lz-restart"
              onClick={() => {
                const ok = window.confirm(
                  spanish
                    ? 'Esto borra las respuestas y vuelve a la primera pregunta.'
                    : 'This clears the answers and returns to the first question.',
                );
                if (ok) {
                  onStartOver();
                }
              }}
            >
              {spanish ? 'Empezar de nuevo' : 'Start over'}
            </button>
          ) : null}
          {question.kind === 'text' ? (
            <button type="submit" className="business-creation-lz-next">
              {spanish ? 'Siguiente' : 'Next'}
            </button>
          ) : null}
        </div>
      </div>
    </form>
  );
};

export const BusinessCreationSummary: React.FC<{
  answers: BusinessCreationAnswers;
  language: FormationLanguage;
  onRevise: () => void;
}> = ({ answers, language, onRevise }) => {
  const spanish = language === 'es';
  const lines = businessCreationAnswerLines(answers, language);
  return (
    <section className="business-creation-summary" aria-label={spanish ? 'Sus respuestas' : 'Your answers'}>
      <div className="business-creation-summary-head">
        <h3>{spanish ? 'Sus respuestas' : 'Your answers'}</h3>
        <button type="button" className="dashboard-security-inline-btn" onClick={onRevise}>
          {spanish ? 'Cambiar respuestas' : 'Change answers'}
        </button>
      </div>
      <ul>
        {lines.map((line) => (
          <li key={line}>
            <DodTermText text={line} language={language} />
          </li>
        ))}
      </ul>
    </section>
  );
};

export const formationDraftFrom = (
  answers: BusinessCreationAnswers,
  id: BusinessQuestionId,
  value: string,
): BusinessCreationAnswers => answerBusinessQuestion(answers, id, value);

export const formationBackFrom = (answers: BusinessCreationAnswers): BusinessCreationAnswers => (
  retreatBusinessAnswers(answers)
);

export default BusinessCreationInterview;
